import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, input, signal, SimpleChanges } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { OrganizationDetailsDTO, ProductItemDTO, UserTypeStatus } from '@core/models';
import { OrganizationsService, WidgetAccessService } from '@core/services';
import { InfoMessagesService } from '@features/info-messages';
import { AccountService } from '@features/server-side-authentication';
import { catchError, finalize, map, Observable, of, tap } from 'rxjs';
import { OrganizationTableDataDTO } from '../../models';

@Component({
  selector: 'app-page-organizations',
  templateUrl: './page-organizations.component.html',
  styleUrl: './page-organizations.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageOrganizationsComponent {
  isLoading = signal<boolean>(false);
  products = signal<ProductItemDTO[]>([]);
  statistics = signal<any>([]);
  selectedOrganization = input<number | null>(null);
  isDetailedView = input.required<boolean>();

  productsAdditionalInfo: any = {
    datahub: { title: 'Discover', icon: 'discovery', order: 1 },
    airflow: { title: 'Schedule', icon: 'schedule', order: 2 },
    snowflake: { title: 'Snowflake', icon: 'snowflake', order: 3 },
    airbyte: { title: 'Extract', icon: 'extract', order: 4 },
    thoughtspot: { title: 'Thoughtspot', icon: 'thoughtspot', order: 5 },
    sftp: { title: 'SFTP', icon: 'sftp', order: 6 },
    qvera: { title: 'Data Bridge', icon: 'data-bridge', order: 7 },
    nifi: { title: 'Ingest', icon: 'nifi', order: 8 },
  };

  dataSources = signal<OrganizationTableDataDTO>({
    dataSource: [],
    title: 'Discover',
    columns: ['name', 'connect_status', 'approval_status', 'actions'],
    organizationId: null,
    url: '',
  });
  connectors = signal<OrganizationTableDataDTO>({
    dataSource: [],
    title: 'Ingest',
    columns: ['name', 'connect_status', 'last_connector_run_end', 'actions'],
    organizationId: null,
    url: '',
  });
  pipelines = signal<OrganizationTableDataDTO>({
    dataSource: [],
    title: 'Publish',
    columns: ['pipeline_name', 'pipeline_status', 'last_run_end', 'actions'],
    organizationId: null,
    url: '',
  });

  snowflakeUrl = toSignal(
    this.organizationsService.selectedOrganizationSnowflakeUrl.pipe(
      map((res) => {
        return res
          ? { ...this.productsAdditionalInfo['snowflake'], link_type: 'Snowflake', url: res.url, use_sso: res.use_sso }
          : null;
      }),
    ),
  );
  thoughtspotUrl = toSignal(
    this.organizationsService.selectedOrganizationThoughtSpotUrl.pipe(
      map((res) => {
        return res ? { ...this.productsAdditionalInfo['thoughtspot'], link_type: 'ThoughtSpot', url: res } : null;
      }),
    ),
  );

  organizationData$!: Observable<OrganizationDetailsDTO | null>;

  public readonly UniversalStatus = UserTypeStatus.Universal;

  get hasAccesOnlyToProductLinks(): boolean {
    return this.widgetsAccessService.hasAccesOnlyToProductLinks();
  }

  constructor(
    private readonly organizationsService: OrganizationsService,
    private readonly widgetsAccessService: WidgetAccessService,
    private readonly infoMessagesService: InfoMessagesService,
    private readonly accountService: AccountService,
  ) {}

  ngOnInit() {
    if (!this.isDetailedView()) {
      this.onChangeOrganization(this.selectedOrganization());
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedOrganization'] && this.selectedOrganization() !== null && this.isDetailedView()) {
      this.onChangeOrganization(this.selectedOrganization());
    }
  }

  setProductsData(productData: ProductItemDTO[] | undefined) {
    if (!productData) {
      return [];
    }
    return productData
      .map((productDataItem) => {
        const productAdditionslInfoItem = this.productsAdditionalInfo[productDataItem.link_type.toLocaleLowerCase()];

        if (productAdditionslInfoItem) {
          return { ...productAdditionslInfoItem, ...productDataItem };
        }

        return { title: productDataItem.link_type, icon: 'extension', order: Number.MAX_SAFE_INTEGER, ...productDataItem };
      })
      .sort((a, b) => a.order - b.order);
  }

  onChangeOrganization(organizationId: any) {
    this.organizationData$ = this.organizationsService.getDashboardData(organizationId).pipe(
      tap((data) => {
        this.products.set(this.setProductsData(data.organization.product_links));

        this.statistics.set([
          {
            name: 'Data Sources',
            amount: data.counts.data_sources.all,
            fullyReady: data.counts.data_sources.fully_ready,
            permission: 'view_datasource',
          },
          {
            name: 'Connectors',
            amount: data.counts.connectors.all,
            fullyReady: data.counts.connectors.fully_ready,
            permission: 'view_connector',
          },
          {
            name: 'Pipelines',
            amount: data.counts.pipelines.all,
            fullyReady: data.counts.pipelines.fully_ready,
            permission: 'view_pipeline',
          },
        ]);

        this.dataSources.update((obj) => ({
          ...obj,
          dataSource: data.data_sources,
          organizationId: data.organization.pk,
          url: this.accountService.isInternal ? `organizations/${data.organization.pk}/data-sources` : 'data-sources',
        }));
        this.connectors.update((obj) => ({
          ...obj,
          dataSource: data.connectors,
          organizationId: data.organization.pk,
          url: this.accountService.isInternal ? `organizations/${data.organization.pk}/connectors` : 'connectors',
        }));
        this.pipelines.update((obj) => ({
          ...obj,
          dataSource: data.pipelines,
          organizationId: data.organization.pk,
          url: this.accountService.isInternal ? `organizations/${data.organization.pk}/pipelines` : 'pipelines',
        }));
      }),
      finalize(() => {
        this.isLoading.set(false);
      }),
      catchError((error: HttpErrorResponse) => {
        if (error.status === HttpStatusCode.NotFound)
          this.infoMessagesService.showError(`Organization with ID '${organizationId}' was not found!`);
        return of(null);
      }),
    );
  }
}
